import React, { useState } from 'react';
import styled, { ThemeProps } from 'styled-components';
import { Theme } from 'styleguide/types';
import { WorkoutContentProps } from '../types';
import { Exercises } from './Exercises';
import { WorkoutMedia } from './WorkoutMedia';
import { colors } from 'styleguide/colors';
import { Spacing } from 'styleguide/spacing';
import LeftArrowSvg from './assets/chevron-left.svg';
import RightArrowSvg from './assets/chevron-right.svg';
import { themeState } from 'styleguide/theme';

type ContainerProps = { hasMedia: boolean };
const Container = styled.div`
  position: relative;
  ${(props: ContainerProps) =>
    props.hasMedia
      ? `@media (min-width: 1024px) {
    height: 300px);
  }
  @media (min-width: 600px) and (max-width: 1023px) {
    height: 375px);
  }
  @media (max-width: 599px) {
    height: 300px);
  }`
      : ''}
`;

const ArrowContainer = styled.div`
  display: flex;
  position: absolute;
  height: 100%;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const LeftArrowContainer = styled.div`
  margin-inline-start: ${Spacing.lg}px;
  cursor: pointer;
  z-index: 3;
`;

const RightArrowContainer = styled.div<ThemeProps<Theme>>`
  margin-inline-end: ${Spacing.lg}px;
  cursor: pointer;
  z-index: 3;
  background-color: ${(props: ThemeProps<Theme>) =>
    props.theme.name === 'dark' ? colors.blackLight : colors.greyUltraLight};
  height: 24px;
  width: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
`;

type ExerciseContainerProps = { hasMedia: boolean };
const ExerciseContainer = styled.div<ExerciseContainerProps>`
  position: relative;
  padding: ${(props: ExerciseContainerProps) => (props.hasMedia ? '0px 56px 32px' : '0px')};
  ${(props: ExerciseContainerProps) =>
    props.hasMedia
      ? `@media (min-width: 1024px) {
    height: calc(300px - ${props.hasMedia ? '32px' : '0px'});
  }
  @media (min-width: 600px) and (max-width: 1023px) {
    height: calc(375px - ${props.hasMedia ? '32px' : '0px'});
  }
  @media (max-width: 599px) {
    height: calc(300px - ${props.hasMedia ? '32px' : '0px'});
  }`
      : ''}
`;

const PagingDotsContainer = styled.div`
  position: absolute;
  left: 16%;
  right: 16%;
  bottom: 0;
  padding-bottom: 16px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  z-index: 3;
  gap: ${Spacing.sm}px;
`;

type PagingDotProps = { isFilled: boolean };
const PagingDot = styled.div<ThemeProps<Theme> & PagingDotProps>`
  height: 8px;
  width: 8px;
  border-radius: 4px;
  background-color: ${(props: ThemeProps<Theme> & PagingDotProps) =>
    props.isFilled
      ? colors.primary
      : props.theme.name === 'dark'
        ? colors.grey
        : colors.greyUltraLight};
`;

export const WorkoutContent = (props: WorkoutContentProps) => {
  const [cellIndex, setCellIndex] = useState(0);

  const { cells, unauthenticatedView } = props;

  if (unauthenticatedView) {
    const exerciseCell = cells.find(c => c.cellType === 'exercises');
    if (!exerciseCell) {
      return <></>;
    }
    const { exercises, truncatedExercisesTitle } = exerciseCell;

    return (
      <Container hasMedia={false}>
        <ExerciseContainer hasMedia={false}>
          <Exercises exercises={exercises} truncatedExercisesTitle={truncatedExercisesTitle} />
        </ExerciseContainer>
      </Container>
    );
  }

  const arrowBgFillColor = themeState.current.name === 'dark' ? colors.white : colors.blackLight;

  const numCells = cells.length;
  const numMediaCells = cells.filter(c => c.cellType === 'media').length;

  const currentCell = cells[cellIndex];

  return (
    <Container hasMedia={numMediaCells > 0}>
      <ArrowContainer>
        {cellIndex > 0 ? (
          <LeftArrowContainer
            onClick={e => {
              e.preventDefault();
              setCellIndex(cellIndex - 1);
            }}
          >
            <LeftArrowSvg
              stroke={arrowBgFillColor}
              style={
                document.documentElement.dir === 'rtl' ? { transform: 'scaleX(-1)' } : undefined
              }
            />
          </LeftArrowContainer>
        ) : (
          <div />
        )}
        {cellIndex < numCells - 1 ? (
          <RightArrowContainer
            onClick={e => {
              e.preventDefault();
              setCellIndex(cellIndex + 1);
            }}
          >
            <RightArrowSvg
              stroke={arrowBgFillColor}
              style={
                document.documentElement.dir === 'rtl' ? { transform: 'scaleX(-1)' } : undefined
              }
            />
          </RightArrowContainer>
        ) : (
          <div />
        )}
      </ArrowContainer>
      {currentCell.cellType === 'media' ? (
        <WorkoutMedia media={currentCell.url} />
      ) : (
        <ExerciseContainer hasMedia={numMediaCells > 0}>
          <Exercises
            exercises={currentCell.exercises}
            truncatedExercisesTitle={currentCell.truncatedExercisesTitle}
          />
        </ExerciseContainer>
      )}
      {numCells > 1 && (
        <PagingDotsContainer>
          {Array(numCells)
            .fill(0)
            .map((_, i) => (
              <PagingDot isFilled={i === cellIndex} key={i}></PagingDot>
            ))}
        </PagingDotsContainer>
      )}
    </Container>
  );
};
